/**
 * @license
 * @copyright Copyright Motili Inc., 2017 All Rights Reserved
 */

import {
    filter as _filter,
    map,
    some,
    uniq,
    union,
    includes,
    merge,
} from 'lodash';
import moment from 'moment-timezone';

import * as ApiService from 'common/services/ApiService';
import * as QuoteService from 'common/services/QuoteService';
import * as AdminDistributionCenterService from 'admin/services/AdminDistributionCentersService';
import * as AdminClientsService from 'admin/services/AdminClientsService';
import {
    hasRole,
    isMotiliAccount,
    isContractorAccount,
    isMotiliFinanceDept,
    isMotiliAdmin,
    isMotiliOwner,
} from 'common/services/AuthService';
import * as DocumentService from 'common/services/DocumentService';
import { QuoteSearchService } from 'common/services/SearchService';
import { isOrderQuote } from 'common/utils/quote-utils';
import { createContainsLoopbackFilter } from 'common/components/data-grid/DataGridUtils';
import {
    CLIENT_SETTINGS,
    QUOTE_STATUS,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { ALLOWED_CLIENT_STATUSES } from 'admin/views/quotes/AdminQuotesAdvancedFilterDataGrid';

export function getCancelReasons() {
    return ApiService.get(true, 'Quotes/cancelReasons');
}

export function getQuotes(filterQuery) {
    const filterFields = {
        filter: {
            include: [
                {
                    relation: 'property',
                    scope: {
                        fields: ['stdAddress', 'stdDeliveryLine'],
                    },
                },
                {
                    relation: 'jobManager',
                    scope: {
                        fields: ['firstName', 'lastName'],
                    },
                },
                {
                    relation: 'contractor',
                    scope: {
                        fields: ['name'],
                    },
                },
                {
                    relation: 'contractorTeamMember',
                    scope: {
                        fields: ['firstName', 'lastName'],
                    },
                },
                {
                    relation: 'user',
                    scope: {
                        fields: ['firstName', 'lastName'],
                    },
                },
                {
                    relation: 'jobCoOwner',
                    scope: {
                        fields: ['id', 'firstName', 'lastName'],
                    },
                },
            ],
            fields: [
                'id',
                'priorityId',
                'documentSubTypeId',
                'number',
                'poNumber',
                'documentTypeId',
                'tradeTypeId',
                'statusId',
                'contacts',
                'extReferenceNumber1',
                'extReferenceNumber2',
                'extReferenceNumber3',
                'extReferenceNumber4',
                'extReferenceNumber5',
                'extPoNumber',
                'propertyId',
                'clientId',
                'client',
                'clientStatusId',
                'contractorId',
                'contractorTeamMemberId',
                'userId',
                'created',
                'updated',
                'opsJobManager',
                'orderedOn',
                'orderedById',
                'tradeSubTypeId',
                'documentOrigination',
                'workOrderOriginationNumber',
                'jobId',
                'coOwner',
                'approvalOwnerId',
                'issueId',
                'projectId',
                'approvalOwnerFullName',
            ],
        },
    };
    const query = merge({}, filterFields, { filter: filterQuery });
    return QuoteSearchService.find(query);
}

export function getCountOfQuotes(query) {
    if (query.where) return QuoteSearchService.count({ where: query.where });
    return QuoteService.count(query);
}

/**
 * retrieves all billings and payments of the work order
 * @param {number} id
 * @returns {Promise<Array>}
 */
export function fetchFinances(id) {
    return ApiService.get(true, `Quotes/${id}/finances`);
}

/**
 * retrieves all auto invoice history of the quote
 * @param {array} invoiceNumbers
 * @returns {Promise<Array>}
 */
export function fetchAutoInvoiceHistory(invoiceNumbers) {
    return ApiService.post(
        true,
        'Quotes/finances/autoInvoiceHistory',
        invoiceNumbers
    );
}

/**
 * retrieves all quote related requirements
 * @param {number} quoteId
 * @returns {Promise<{clientRequirements: Array, requirements: Array}>}
 */
export function getQuoteRelatedRequirements(quoteId) {
    return QuoteService.getQuoteRelatedRequirements(quoteId);
}

/**
 * retrieves all quote related notes
 * @param {number} quoteId
 * @param {object} [documentOrigination]
 * @returns {Promise<Array>}
 */
export function getQuoteNotes(quoteId, documentOrigination) {
    return QuoteService.getQuoteNotes(quoteId, documentOrigination);
}

/**
 * retrieves all quote related comments
 * @param {number} jobId
 * @returns {Promise<Array>}
 */
export function getQuoteComments(jobId) {
    return QuoteService.getQuoteComments(jobId);
}

/**
 * trigger auto invoice
 * @param {number} clientId
 * @param {number} historyId
 * @returns {Promise<Object>}
 */
export function triggerAutoInvoice(clientId, historyId) {
    return ApiService.get(
        true,
        `Quotes/finances/${clientId}/triggerAutoInvoice/${historyId}`
    );
}

export async function processFilters(
    searchFilters,
    advancedFilters,
    activeQuery,
    userId
) {
    const query = merge({}, activeQuery, {
        where: {
            and: [{ documentSubTypeId: { neq: 'EQUIPMENT_ONLY' } }],
        },
    });

    if (
        !Object.keys(searchFilters).length &&
        !Object.keys(advancedFilters).length
    ) {
        return query;
    }

    // Search filter section
    const filterGroups = {};

    const containsFilterMap = {
        APPROVAL_OWNER: 'approvalOwnerFullName',
        CLIENT: 'client',
        EXT_REFERENCE_NUMBER_1: 'extReferenceNumber1',
        EXT_REFERENCE_NUMBER_2: 'extReferenceNumber2',
        EXT_REFERENCE_NUMBER_3: 'extReferenceNumber3',
        EXT_REFERENCE_NUMBER_4: 'extReferenceNumber4',
        EXT_REFERENCE_NUMBER_5: 'extReferenceNumber5',
        NUMBER: 'number',
    };

    const inqFilterMap = {
        ADDRESS: 'propertyId',
        CONTRACTOR: 'contractorId',
        CO_OWNER: 'coOwner',
        JOB_NUMBER: 'jobId',
        OWNER: 'opsJobManager',
        PROJECT: 'jobId',
        TECHNICIAN: 'contractorTeamMemberId',
    };

    Object.keys(searchFilters).forEach(key => {
        if (!filterGroups[key]) {
            filterGroups[key] = { or: [] };
        }

        if (containsFilterMap[key]) {
            filterGroups[key].or.push(
                ...searchFilters[key].map(
                    filter =>
                        createContainsLoopbackFilter(
                            containsFilterMap[key],
                            filter.term
                        ).where
                )
            );
            return;
        }

        if (inqFilterMap[key]) {
            filterGroups[key].or.push({
                [inqFilterMap[key]]: {
                    inq: searchFilters[key].flatMap(filter => filter.term),
                },
            });
        }
    });

    Object.values(filterGroups).forEach(group => {
        if (group.or.length > 0) {
            query.where.and.push(group);
        }
    });

    // Advanced filter section
    const typeFilter = { or: [] };
    const statusFilter = { or: [] };
    const segmentsFilter = [];
    const lineBusinessFilter = [];
    const issueFilter = { or: [] };
    const issueForms = [];
    const issueReasonForms = [];
    const issueSecondaryReasonForms = [];

    Object.keys(advancedFilters).forEach(key => {
        switch (key) {
            case 'excludeClient':
                advancedFilters[key].forEach(filter => {
                    query.where.and.push({ clientId: { neq: filter.term } });
                });
                break;
            case 'businessSegment':
                advancedFilters[key].forEach(filter => {
                    segmentsFilter.push(filter.form);
                });
                break;
            case 'lineOfBusiness':
                advancedFilters[key].forEach(filter => {
                    lineBusinessFilter.push(filter.form);
                });
                break;
            case 'occupied':
                query.where.and.push({
                    occupied: advancedFilters[key].term === 'Occupied',
                });
                break;
            case 'type':
                advancedFilters[key].forEach(filter => {
                    typeFilter.or.push({ documentSubTypeId: filter.term });
                });
                break;
            case 'status':
                advancedFilters[key].forEach(filter => {
                    statusFilter.or.push({ statusId: filter.term });
                });
                break;
            case 'ownerIs':
                if (advancedFilters[key].term === 'Me') {
                    query.where.and.push({ opsJobManager: userId });
                }
                break;
            case 'approvalOwner':
                if (advancedFilters[key].term === 'Me') {
                    query.where.and.push({ approvalOwnerId: userId });
                }
                break;
            case 'created90Days':
                query.where.and.push({
                    created: {
                        gt: moment().subtract(90, 'days'),
                        lt: moment().add(1, 'days'),
                    },
                });
                break;
            case 'createdBefore':
                query.where.and.push({
                    created: {
                        lt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'createdAfter':
                query.where.and.push({
                    created: {
                        gt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'planned':
                if (advancedFilters[key].term === 'Planned') {
                    query.where.and.push({ projectId: { neq: null } });
                } else {
                    query.where.and.push({ projectId: null });
                }
                break;
            case 'country':
                query.where.and.push({ country: advancedFilters[key].term });
                break;
            case 'issue':
                advancedFilters[key].forEach(filter => {
                    if (filter.term === 'NONE') {
                        issueFilter.or.push(
                            { issueId: null },
                            { issueId: { like: `%[]%` } }
                        );
                    } else {
                        issueFilter.or.push({
                            issueId: { like: `%${filter.term}%` },
                        });
                        issueForms.push(filter.form);
                    }
                });
                break;
            case 'issueReason':
                advancedFilters[key].forEach(filter => {
                    issueReasonForms.push(filter.form);
                });
                break;
            case 'issueSecondaryReason':
                advancedFilters[key].forEach(filter => {
                    issueSecondaryReasonForms.push(filter.form);
                });
                break;
            default:
        }
    });

    query.where.and.push(typeFilter, statusFilter, issueFilter);

    if (segmentsFilter.length || lineBusinessFilter.length) {
        let clientIdArray = [];
        clientIdArray = union(
            clientIdArray,
            await AdminClientsService.getClientIdBySettingsType(
                segmentsFilter,
                lineBusinessFilter
            )
        );
        const clientIdFilter = { clientId: { inq: clientIdArray } };
        query.where.and.push({ or: [clientIdFilter] });
    }

    if (
        issueForms.length &&
        (issueReasonForms.length || issueSecondaryReasonForms.length)
    ) {
        const quoteIdsFromIssues = await getQuoteIdsByIssue(
            issueForms,
            issueReasonForms,
            issueSecondaryReasonForms
        );
        if (quoteIdsFromIssues.length) {
            query.where.and.push({ id: { inq: quoteIdsFromIssues } });
        } else {
            query.where.and.push({ id: -1 }); // case not found quote from issue conditions
        }
    }

    return query;
}

export async function processFiltersEquipmentOnly(
    searchFilters,
    advancedFilters,
    activeQuery
) {
    if (
        !Object.keys(searchFilters).length &&
        !Object.keys(advancedFilters).length
    ) {
        return activeQuery;
    }

    const query = merge({}, activeQuery, {
        where: {
            and: [],
        },
    });

    // Search filter section
    const filterGroups = {};

    const containsFilterMap = {
        CLIENT: 'client',
        EXT_PO_NUMBER: 'extPoNumber',
        NUMBER: 'number',
        REF1: 'extReferenceNumber1',
        REF2: 'extReferenceNumber2',
        REF3: 'extReferenceNumber3',
        REF4: 'extReferenceNumber4',
        REF5: 'extReferenceNumber5',
    };

    Object.keys(searchFilters).forEach(key => {
        if (!filterGroups[key]) {
            filterGroups[key] = { or: [] };
        }

        if (containsFilterMap[key]) {
            filterGroups[key].or.push(
                ...searchFilters[key].map(
                    filter =>
                        createContainsLoopbackFilter(
                            containsFilterMap[key],
                            filter.term
                        ).where
                )
            );
            if (key === 'CLIENT') {
                query.where.and.push({
                    clientStatusId: { inq: ALLOWED_CLIENT_STATUSES },
                });
            }
        }
    });

    Object.values(filterGroups).forEach(group => {
        if (group.or.length > 0) {
            query.where.and.push(group);
        }
    });

    // Advanced filter section
    const statusFilter = { or: [] };

    Object.keys(advancedFilters).forEach(key => {
        switch (key) {
            case 'excludeClient':
                advancedFilters[key].forEach(filter => {
                    query.where.and.push({ clientId: { neq: filter.term } });
                });
                break;
            case 'status':
                advancedFilters[key].forEach(filter => {
                    statusFilter.or.push({ statusId: filter.term });
                });
                break;
            case 'createdBefore':
                query.where.and.push({
                    created: {
                        lt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'createdAfter':
                query.where.and.push({
                    created: {
                        gt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'planned':
                if (advancedFilters[key].term === 'Planned') {
                    query.where.and.push({ projectId: { neq: null } });
                } else {
                    query.where.and.push({ projectId: null });
                }
                break;
            case 'country':
                query.where.and.push({ country: advancedFilters[key].term });
                break;
            default:
        }
    });

    if (statusFilter.or.length) {
        query.where.and.push(statusFilter);
    }

    return query;
}

async function getQuoteIdsByIssue(
    issueForms = [],
    issueReasonForms = [],
    issueSecondaryReasonForms = []
) {
    const _conditions = map(issueForms, issueIdForm => {
        const _condition = {
            and: [{ issueId: issueIdForm.id }, { active: true }],
        };
        const relateReasonIds = map(issueIdForm.reasons, res => res.id);
        const relateReasonForms = _filter(issueReasonForms, issueReasonForm =>
            relateReasonIds.includes(issueReasonForm.id)
        );
        if (relateReasonForms && relateReasonForms.length > 0) {
            const _reasonClause = {
                or: [],
            };
            relateReasonForms.forEach(relReaForm => {
                _reasonClause.or.push({
                    issueReasonId: { like: `%${relReaForm.id}%` },
                });
            });
            _condition.and.push(_reasonClause);
            const relateSecondaryReasonForms = _filter(
                issueSecondaryReasonForms,
                issueSecondaryForm => {
                    for (let i = 0; i < relateReasonForms.length; i++) {
                        if (
                            some(relateReasonForms[i].reasons, {
                                id: issueSecondaryForm.id,
                            })
                        ) {
                            return true;
                        }
                    }
                    return false;
                }
            );
            if (
                relateSecondaryReasonForms &&
                relateSecondaryReasonForms.length > 0
            ) {
                const _secReasonClause = {
                    or: [],
                };
                relateSecondaryReasonForms.forEach(relSecReaForm => {
                    _secReasonClause.or.push({
                        issueSecondaryReasonId: {
                            like: `%${relSecReaForm.id}%`,
                        },
                    });
                });
                _condition.and.push(_secReasonClause);
            }
        }

        return _condition;
    });

    const filter = {
        fields: ['quoteId'],
        where: { and: [{ or: _conditions }] },
    };

    const issueTracking = await QuoteService.fetchQuoteIssueTracking(filter);
    return uniq(map(issueTracking, i => i.quoteId));
}

export function getQuote(id) {
    let _quote;
    return QuoteService.fetchQuoteByFilterId(id, 'BASE')
        .then(quote => {
            _quote = quote;
            const promises = [];
            const distributionCenterQuery = {
                filter: {
                    fields: [
                        'vendorBranchNumber',
                        'vendorBranchName',
                        'stdAddress',
                        'stdDeliveryLine',
                        'stdCity',
                        'stdPostalCode',
                        'stdRegion',
                        'vendorId',
                    ],
                    include: ['vendor'],
                },
            };
            if (
                !_quote.fulfillmentDetails ||
                !_quote.fulfillmentDetails.distributionCenterId
            ) {
                return { ..._quote, vendorDistributionCenter: null };
            }
            promises.push(
                AdminDistributionCenterService.getDistributionCenter(
                    _quote.fulfillmentDetails.distributionCenterId,
                    distributionCenterQuery
                )
            );
            return Promise.all(promises);
        })
        .then(distributionCenter => ({
            ..._quote,
            vendorDistributionCenter: distributionCenter[0],
        }));
}

export function submit(id, reason, reasonId) {
    return QuoteService.submit(id, true, reason, reasonId);
}

export function cancel(id, reason, reasonId) {
    return QuoteService.cancel(id, reason, reasonId);
}

export function reviewed(id) {
    return QuoteService.reviewed(id);
}

export function generatePdf(
    id,
    fileName,
    statementOfWork,
    selectedLineItemIds,
    bundlePricing,
    quotePdfType,
    selectedPhotoIds
) {
    return QuoteService.generatePdf(
        id,
        fileName,
        statementOfWork,
        selectedLineItemIds,
        bundlePricing,
        quotePdfType,
        selectedPhotoIds
    );
}

export function requestRevision(
    id,
    reasonId,
    subReasonId,
    triReasonId,
    otherReason
) {
    return QuoteService.requestRevision(
        id,
        reasonId,
        subReasonId,
        triReasonId,
        otherReason
    );
}

export function generateOrderQuotePdf(
    id,
    fileName,
    statementOfWork,
    selectedLineItemIds,
    hidePricing,
    hideBundleItems,
    includeSpecSheets,
    includeWarrantySheets,
    quoteLifeSpan
) {
    return QuoteService.generateOrderQuotePdf(
        id,
        fileName,
        statementOfWork,
        selectedLineItemIds,
        hidePricing,
        hideBundleItems,
        includeSpecSheets,
        includeWarrantySheets,
        quoteLifeSpan
    );
}

export function requestApproval(id, data) {
    return QuoteService.requestApproval(id, data);
}

export function reject(id, reasonId, subReasonId, otherReason) {
    return QuoteService.reject(id, reasonId, subReasonId, otherReason);
}

export function approve(id, homeownerResponsibility, extPoNumber) {
    return QuoteService.approve(id, homeownerResponsibility, extPoNumber);
}

export function approveWithHold(id, homeownerResponsibility, extPoNumber) {
    return QuoteService.approveWithHold(
        id,
        homeownerResponsibility,
        extPoNumber
    );
}

export function order(id) {
    return QuoteService.order(id);
}

export function createNote(id, data, addToComment, editable = true) {
    const _note = {
        ...data,
    };
    return QuoteService.createNote(id, _note, addToComment, editable);
}

export function updateNote(id, note, mentionsData) {
    const _note = {
        message: note.message,
        pinned: note.pinned,
        mentionsData,
    };
    return QuoteService.updateNote(id, note.id, _note);
}

export function createComment(id, comment, mentionsData, issueTrackingId) {
    const _comment = {
        message: comment.note,
        messageType: 'MOTILI',
        mentionsData,
        issueTrackingId,
    };
    return QuoteService.createComment(id, _comment);
}

export function updateComment(id, comment, mentionsData) {
    const _comment = {
        message: comment.message,
        mentionsData,
    };
    return QuoteService.updateComment(id, comment.id, _comment);
}

/**
 *
 * @param {number} id
 * @param {string} ownerId
 * @param {string} coOwnerId
 * @param {string} approvalOwnerId
 * @returns {Promise<Object>}
 */
export function transferOwner(id, ownerId, coOwnerId, approvalOwnerId) {
    return QuoteService.transferOwner(id, ownerId, coOwnerId, approvalOwnerId);
}

export function changePriority(
    orderId,
    priorityId,
    reasonId,
    reasonIdLabel,
    reason
) {
    return QuoteService.changePriority(
        orderId,
        priorityId,
        reasonId,
        reasonIdLabel,
        reason
    );
}

export function changeQuoteType(
    id,
    quoteTypeId,
    reasonId,
    reasonIdLabel,
    reason
) {
    return QuoteService.changeQuoteType(
        id,
        quoteTypeId,
        reasonId,
        reasonIdLabel,
        reason
    );
}

/**
 * @param {Number} id
 * @param {*} requirement
 *
 * @returns {undefined}
 */
export function createPhoto(id, requirement) {
    return QuoteService.createPhoto(id, requirement);
}

export function deletePhoto(id, photoId) {
    return QuoteService.deletePhoto(id, photoId);
}

export function editUploadedPhotoRequirement(id, photoId, updateData) {
    return QuoteService.editUploadedPhotoRequirement(id, photoId, updateData);
}

export function removeLineItem(quoteId, lineItemId) {
    return QuoteService.deleteLineItem(quoteId, lineItemId).then(() =>
        getQuote(quoteId)
    );
}

export function removeLineItems(quoteId, lineItemIds, reason) {
    return QuoteService.deleteLineItems(quoteId, lineItemIds, reason);
}

export function bulkAdjustLineItems(
    quoteId,
    bulkAdjustment,
    adjustmentReason,
    adjustmentReasonNote
) {
    return QuoteService.bulkAdjustLineItems(
        quoteId,
        bulkAdjustment,
        adjustmentReason,
        adjustmentReasonNote
    );
}

export function addLineItems(quoteId, lineItems) {
    // lineItems is of form {product, supplied}
    const createLineItems = lineItems.map(async p => {
        const _p = {
            ...p,
            sku: p.product.sku,
            suppliedQuantity: p.supplied.quantity,
            suppliedDescription: p.supplied.description,
            suppliedPayout: p.supplied.suppliedPayout,
            suppliedVendorCost: p.supplied.suppliedVendorCost,
            img: p.product.img,
            details: p.product.details,
        };
        const createdLineItem = await QuoteService.createLineItem(quoteId, _p);
        if (
            (p.payout && p.payout.adjustedPayout) ||
            (p.price && p.price.adjustedPrice)
        ) {
            const adjustment = {
                adjustedVendorCostAdjustmentTypeId:
                    p.cost && p.cost.adjustedVendorCostAdjustmentTypeId,
                adjustedPayoutAdjustmentTypeId: 'FIXED',
                adjustedPriceAdjustmentTypeId:
                    p.cost && p.cost.adjustedPriceAdjustmentTypeId,
                adjustedPriceAdjustment:
                    p.cost && p.cost.adjustedPriceAdjustment,
                adjustedPriceTaxPercent:
                    p.cost && p.cost.adjustedPrice
                        ? parseFloat(p.cost.adjustedPriceTax) /
                          p.cost.adjustedPrice
                        : 0,
                adjustedPayoutAdjustment: p.payout && p.payout.adjustedPayout,
            };
            await adjustPayoutPriceLineItem(
                quoteId,
                createdLineItem.id,
                adjustment
            );
        }

        return createdLineItem;
    });
    return Promise.all(createLineItems);
}

/**
 * update a line item
 * @param {number} id
 * @param {number} lineItemId
 * @param {object} lineItem
 * @returns {Promise<Object>}
 */
export function updateLineItem(id, lineItemId, lineItem) {
    return QuoteService.updateLineItem(id, lineItemId, lineItem);
}

/**
 * adjust a line item
 * @param {number} id
 * @param {number} lineItemId
 * @param {object} adjustment
 * @param {boolean} finance
 *
 * @returns {undefined}
 */
export function adjustFinanceLineItem(id, lineItemId, adjustment, finance) {
    return QuoteService.adjustFinanceLineItem(
        id,
        lineItemId,
        adjustment,
        finance
    );
}

/**
 * adjust a line item
 * @param {number} id
 * @param {number} lineItemId
 * @param {object} adjustment
 * @param {boolean} finance
 *
 * @returns {undefined}
 */
export function adjustPayoutPriceLineItem(id, lineItemId, adjustment, finance) {
    return QuoteService.adjustPayoutPriceLineItem(
        id,
        lineItemId,
        adjustment,
        finance
    );
}

/**
 * unapprove a line item
 * @param {Number} id
 * @param {Number} lineItemId
 * @param {String} reason
 *
 * @returns {undefined}
 */
export function unapproveLineItem(id, lineItemId, reason) {
    return QuoteService.unapproveLineItem(id, lineItemId, reason);
}

export function patch(id, _patch) {
    return QuoteService.patch(id, _patch);
}

export function mapFulfillmentDetailsFromTruck(quote, fulfillmentDetails) {
    return QuoteService.mapFulfillmentDetailsFromTruck(
        quote,
        fulfillmentDetails
    );
}

export function shouldDisplayActionSubmit(quote) {
    if (!quote) {
        return false;
    }
    const statusId = quote.statusId;
    return statusId === 'CREATED' || statusId === 'REVISING';
}

export function shouldDisplayActionCancel(quote) {
    if (!quote) {
        return false;
    }
    const statusId = quote.statusId;
    if (
        statusId === 'CREATED' ||
        statusId === 'SUBMITTED' ||
        statusId === 'REVISING' ||
        statusId === 'REVIEWED' ||
        statusId === 'APPROVED'
    ) {
        if (isMotiliAdmin() || isMotiliFinanceDept()) {
            return true;
        }
    }
    return false;
}

export function shouldDisplayActionReviewed(quote) {
    if (!quote) {
        return false;
    }
    // NM-1776: Allow Quote No Origin to action REVIEWED
    const acceptableRoles = ['motili_owner', 'motili_admin', 'motili_manager'];
    const statusId = quote.statusId;
    return statusId === 'SUBMITTED' && hasRole(acceptableRoles);
}

export function shouldDisplayActionRequestRevision(quote) {
    if (!quote) {
        return false;
    }
    const quoteTypeId = quote.quoteTypeId;
    if (quoteTypeId === 'REPAIR' || quoteTypeId === 'REPLACE') {
        const statusId = quote.statusId;
        const acceptableRoles = [
            'motili_owner',
            'motili_admin',
            'motili_manager',
        ];
        const isAssigned =
            !!quote.contractorId && !!quote.contractorTeamMemberId;
        return (
            statusId === 'SUBMITTED' && hasRole(acceptableRoles) && isAssigned
        );
    }
    return false;
}

export function shouldDisplayActionReject(quote) {
    if (!quote) {
        return false;
    }
    // NM-1776: Allow Quote No Origin to action REVIEWED
    const statusId = quote.statusId;
    const acceptableRoles = ['motili_owner', 'motili_admin', 'motili_manager'];
    return statusId === 'REVIEWED' && hasRole(acceptableRoles);
}

export function shouldDisplayActionApprove(quote) {
    if (!quote) return false;

    const statusId = quote.statusId;
    const clientStatusId = quote.client ? quote.client.statusId : '';
    const isValidClientStatus =
        clientStatusId === 'APPROVED' || clientStatusId === 'ENABLED';
    const acceptableRoles = ['motili_owner', 'motili_admin', 'motili_manager'];
    const hasExceptionFlag = quote.issueId?.length > 0;
    const enableQuoteApproval = quote.client?.settings?.find(
        setting =>
            setting.settingTypeId === CLIENT_SETTINGS.EQUIPMENT_ONLY_QUOTE
    )?.setting?.enableQuoteApproval;
    const equipmentOnlyApprovalSetting = isOrderQuote(quote.quoteTypeId)
        ? enableQuoteApproval
        : true;

    return (
        (statusId === 'REVIEWED' ||
            statusId === 'SUBMITTED' ||
            (isOrderQuote(quote?.quoteTypeId) && statusId === 'CREATED')) &&
        hasRole(acceptableRoles) &&
        isValidClientStatus &&
        !hasExceptionFlag &&
        equipmentOnlyApprovalSetting
    );
}

export function shouldDisplayActionOrder(quote) {
    if (!quote) {
        return false;
    }
    const statusId = quote.statusId;
    const clientStatusId = quote.client ? quote.client.statusId : '';
    const isValidClientStatus =
        clientStatusId === 'APPROVED' || clientStatusId === 'ENABLED';
    const acceptableRoles = ['motili_owner', 'motili_admin', 'motili_manager'];
    return (
        statusId === 'APPROVED' &&
        hasRole(acceptableRoles) &&
        isValidClientStatus
    );
}

/**
 * determines if line item action remove is available
 * @param {string} quoteStatusId
 * @param {string} lineItemStatusId
 * @param {number} bundleJobLineItemId
 * @param {string} productCategoryId
 * @return {Boolean}
 */
export function shouldDisplayLineItemActionRemove(
    quoteStatusId,
    lineItemStatusId
) {
    if (!quoteStatusId || !lineItemStatusId) {
        return false;
    }

    if (['CREATED', 'REVISING', 'DRAFTED'].indexOf(quoteStatusId) !== -1) {
        if (
            lineItemStatusId === 'CREATED' &&
            (isMotiliAccount() || isContractorAccount())
        ) {
            return true;
        }
    }
    if (['SUBMITTED', 'REVIEWED'].indexOf(quoteStatusId) !== -1) {
        if (lineItemStatusId === 'CREATED' && isMotiliAccount()) {
            return true;
        }
    }
    if (
        quoteStatusId === 'APPROVED' &&
        isMotiliAdmin() &&
        lineItemStatusId === 'CREATED'
    )
        return true;

    return false;
}

/**
 * determines if line item action unapprove is available
 * @param {object} quote
 * @param {object} lineItem
 * @returns {Boolean}
 */
export function shouldDisplayLineItemActionUnapprove(quote, lineItem) {
    if (
        !quote ||
        !quote.lineItems ||
        !lineItem ||
        lineItem.bundleJobLineItemId
    ) {
        return false;
    }

    const acceptableQuoteStatuses = [
        'CREATED',
        'DRAFTED',
        'REVISING',
        'SUBMITTED',
        'REVIEWED',
        'APPROVED',
    ];
    const acceptableRoles = ['motili_owner', 'motili_admin'];
    if (lineItem.statusId === 'APPROVED') {
        if (
            hasRole(acceptableRoles) &&
            acceptableQuoteStatuses.indexOf(quote.statusId) !== -1
        ) {
            return !(
                lineItem.productCategoryId === 'BUNDLE' &&
                quote.lineItems
                    .filter(li => li.bundleJobLineItemId === lineItem.id)
                    .some(li =>
                        [
                            'COMPLETED',
                            'PARTIALLY_RETURNED',
                            'RETURNED',
                            'RETURNING',
                        ].includes(li.statusId)
                    )
            );
        }
    }
    return false;
}

/**
 * determines if line item action edit is available
 * @param {String} quoteStatusId
 * @param {String} lineItemStatusId
 * @returns {Boolean}
 */
export function shouldDisplayLineItemActionEdit(
    quoteStatusId,
    lineItemStatusId
) {
    const acceptableQuoteStatuses = [
        'CREATED',
        'DRAFTED',
        'REVISING',
        'SUBMITTED',
        'REVIEWED',
        'APPROVED',
    ];
    const acceptableLineItemStatuses = ['CREATED', 'APPROVED'];
    const acceptableRoles = ['motili_owner', 'motili_admin'];
    if (
        acceptableQuoteStatuses.indexOf(quoteStatusId) !== -1 &&
        acceptableLineItemStatuses.indexOf(lineItemStatusId) !== -1
    ) {
        if (hasRole(acceptableRoles)) {
            return true;
        }
    }
    return false;
}

/**
 * determines if line item can edit quantity
 * @param {array} lineItems
 * @param {object} lineItem
 * @returns {Boolean}
 */
export function shouldAllowEditLineItemQuantity(lineItems, lineItem) {
    if (
        lineItem.productCategoryId === 'BUNDLE' &&
        lineItems
            .filter(li => li.bundleJobLineItemId === lineItem.id)
            .some(li =>
                [
                    'COMPLETED',
                    'PARTIALLY_RETURNED',
                    'RETURNED',
                    'RETURNING',
                ].includes(li.statusId)
            )
    ) {
        return false;
    }
    return !lineItem.bundleJobLineItemId;
}

/**
 * @returns {boolean} true if cancelled, last document, and user isMotili
 */
export function shouldDisplayActionClone() {
    // const maxSeq = Math.max(
    //     ...quote.job.sequence.map(seq => seq.sequenceNumber)
    // );
    // const lastDocument = quote.job.sequence.find(
    //     seq => seq.sequenceNumber === maxSeq
    // );
    // return (
    //     isMotiliAccount() &&
    //     quote.id === lastDocument.documentId &&
    //     quote.statusId === 'CANCELLED'
    // );
    // Disable clone and using reopen instead
    return false;
}

/**
 * @param {object} quote
 * @returns {boolean} true if cancelled, last document, and user isMotili
 */
export function shouldDisplayActionReopen(quote) {
    const _sequence = quote?.job?.sequence || [];
    const maxSeq = Math.max(..._sequence.map(seq => seq.sequenceNumber));
    const lastDocument = _sequence.find(seq => seq.sequenceNumber === maxSeq);
    return (
        isMotiliAccount() &&
        quote.id === lastDocument.documentId &&
        quote.statusId === 'CANCELLED'
    );
}

/**

 * @param {object} quote
 * @returns {boolean} true if CREATED, SUBMITTED or REVISING and user isMotili
 */
export function shouldDisplayActionApplyTemplate(quote) {
    const allowStatuses = [
        QUOTE_STATUS.CREATED,
        QUOTE_STATUS.SUBMITTED,
        QUOTE_STATUS.REVISING,
    ];
    return (
        (isMotiliAdmin() || isMotiliOwner()) &&
        allowStatuses.includes(quote.statusId)
    );
}

/**
 * determines if quote action transferOwner is available
 * @param {Object} quote
 * @param {String} quote.statusId
 * @returns {Boolean}
 */
export function shouldDisplayActionTransferOwner(quote) {
    if (!quote) {
        return false;
    }
    return quote.statusId !== 'CLOSED' && quote.statusId !== 'CANCELLED';
}

export function shouldDisplayActionChangePriority(quote) {
    if (!quote) {
        return false;
    }
    return (
        isMotiliAccount() &&
        quote.statusId !== 'CLOSED' &&
        quote.statusId !== 'CANCELLED'
    );
}

export function shouldDisplayActionApproveDraft(quote) {
    if (!quote) {
        return false;
    }
    return isMotiliOwner() && quote.statusId === 'DRAFTED';
}

export function shouldDisplayActionCancelDraft(quote) {
    if (!quote) {
        return false;
    }
    return isMotiliOwner() && quote.statusId === 'DRAFTED';
}

export function shouldDisplayActionExceptionFlag(quote) {
    if (!quote) {
        return false;
    }
    if (quote.issueId && quote.issueId.includes('QUALITY_APPROVAL_REQUIRED')) {
        return false;
    }
    return (
        quote.statusId !== 'CLOSED' &&
        quote.statusId !== 'CANCELLED' &&
        quote.statusId !== 'DRAFT_CANCELLED' &&
        quote.statusId !== 'APPROVED'
    );
}

export function shouldDisplayActionChangeSubCategory(quote) {
    if (!quote) {
        return false;
    }
    return isMotiliAccount();
}

export function shouldDisplayActionAssign(quote) {
    const statusOK = [
        'CREATED',
        'SUBMITTED',
        'REVIEWED',
        'REVISING',
        'APPROVED',
        'ORDERED',
        'COMPLETED',
    ].includes(quote.statusId);

    const typeOK = [
        'NO_ORIGIN_REPLACE',
        'NO_ORIGIN_REPAIR',
        'REPAIR',
        'REPLACE',
    ].includes(quote.quoteTypeId);

    const rolesOk = hasRole(['motili_owner', 'motili_admin']);

    const assigned = quote.contractorId || quote.contractorTeamMemberId;

    return statusOK && typeOK && rolesOk && !assigned;
}

export function shouldDisplayActionUnassign(quote) {
    const statusOK = ['CREATED', 'SUBMITTED', 'REVIEWED', 'REVISING'].includes(
        quote.statusId
    );

    const typeOK = [
        'NO_ORIGIN_REPLACE',
        'NO_ORIGIN_REPAIR',
        'REPAIR',
        'REPLACE',
    ].includes(quote.quoteTypeId);

    const rolesOk = hasRole(['motili_owner', 'motili_admin']);

    const assigned = quote.contractorId || quote.contractorTeamMemberId;

    return statusOK && typeOK && rolesOk && assigned;
}

export function getDocumentByJobNumber(jobNumber) {
    if (!jobNumber) return Promise.resolve([]);
    return DocumentService.getDocumentByJobNumber('Quotes', jobNumber, {
        fields: ['id', 'jobId'],
    });
}

export function getDocumentByJobFilter(filterQuery = {}) {
    return DocumentService.getDocumentByJobFilter('jobs', {
        fields: ['id'],
        where: filterQuery,
    });
}

export function getPriorityTypes() {
    return ApiService.get(true, 'Quotes/priorities').then(res =>
        res.map(item => ({ display: item.display, id: item.id }))
    );
}
export function getPriorityChangeReasonTypes() {
    return ApiService.get(true, 'Quotes/priorityChangeReasons').then(res =>
        res.map(item => ({ display: item.display, id: item.id }))
    );
}

export function getQuoteTypeChangeReasons() {
    return ApiService.get(true, 'quotes/quoteTypeChangeReasons').then(res =>
        res.map(item => ({ display: item.display, id: item.id }))
    );
}

/**
 *
 * @param {*} id
 * @param {*} contractorId
 * @param {*} contractorTeamMemberId
 * @param {*} reason
 * @returns {Promise<Object>}
 */
export function assign(id, contractorId, contractorTeamMemberId, reason) {
    return QuoteService.assign(
        id,
        contractorId,
        contractorTeamMemberId,
        reason
    );
}

/**
 * @param {number} id
 * @param {string} reason
 * @returns {Promise<Object>} new cloned quote
 */
export function clone(id, reason) {
    return QuoteService.clone(id, reason);
}

/**
 * @param {number} id
 * @param {string} reason
 * @param {Object} [updates]
 * @returns {Promise<Object>} new cloned quote
 */
export function cloneEquipmentOnlyQuote(id, reason, updates) {
    return QuoteService.cloneEquipmentOnlyQuote(id, reason, updates);
}

/**
 * @param {number} id
 * @param {string} reason
 * @returns {Promise<Object>} reopened quote
 */
export function reopen(id, reason) {
    return QuoteService.reopen(id, reason);
}

/**
 *
 * @param {*} id
 * @param {*} reason
 * @returns {Promise<Object>}
 */
export function unassign(id, reason) {
    return QuoteService.unassign(id, reason);
}

/**
 * determines if quote action schedule is available
 * @param {Object} quote
 * @param {String} quote.statusId
 * @returns {Boolean}
 */
export function shouldDisplayActionSchedule(quote) {
    if (!quote) {
        return false;
    }
    return (
        includes(
            ['CREATED', 'SUBMITTED', 'REVIEWED', 'REVISING'],
            quote.statusId
        ) && isMotiliAccount()
    );
}

/**
 * Set schedule for quote
 * @param {number|string} id
 * @param {string} startDateTime
 * @param {string} [reason]
 * @returns {Promise<Object>}
 */
export function schedule(id, startDateTime, reason) {
    const _schedule = {
        type: 'MOTILI_SCHEDULE',
        startDateTime,
    };
    return QuoteService.schedule(id, _schedule, reason);
}

export function shouldDisplayManageAction(quote) {
    if (!quote) {
        return false;
    }

    return (
        isMotiliAccount() &&
        quote.statusId !== 'CLOSED' &&
        quote.statusId !== 'CANCELLED' &&
        quote.statusId !== 'DRAFT_CANCELLED' &&
        quote.statusId !== 'DRAFTED'
    );
}

export function shouldDisplayActionChangeProject(quote) {
    if (!quote) {
        return false;
    }
    return (
        isMotiliAccount() &&
        quote.statusId !== 'CLOSED' &&
        quote.statusId !== 'CANCELLED'
    );
}

export function shouldDisplayActionChangeQuoteType(quote) {
    if (!quote) {
        return false;
    }

    const acceptableQuoteStatuses = [
        'CREATED',
        'SUBMITTED',
        'REVIEWED',
        'REVISING',
        'WAITING_FOR_APPROVAL',
    ];

    return (
        isMotiliAccount() &&
        acceptableQuoteStatuses.includes(quote.statusId) &&
        (quote.quoteTypeId === 'REPAIR' || quote.quoteTypeId === 'REPLACE')
    );
}

export function changeProject(id, projectId) {
    return QuoteService.changeProject(id, projectId);
}

export function bulkDelete(id, lineItems) {
    return ApiService.post(
        true,
        `Quotes/${id}/lineItems/bulkDelete`,
        lineItems
    );
}

export function getDownloadAllPhotoUrl(id) {
    return ApiService.get(true, `Quotes/${id}/downloadAllPhoto`);
}

/**
 * Get clone quote options
 * @param {number} quoteId
 * @returns {Promise}
 */
export function getCloneOptions(quoteId) {
    return ApiService.get(true, `Quotes/${quoteId}/cloneOptions`);
}

/**
 *
 * @param {number} id
 * @returns {Promise<Object>}
 */
export function approveDraft(id) {
    return ApiService.post(true, `Quotes/${id}/approveDraft`);
}

/**
 *
 * @param {number} id
 * @returns {Promise<Object>}
 */
export function cancelDraft(id) {
    return ApiService.post(true, `Quotes/${id}/cancelDraft`);
}

export function updateDraft(id, data) {
    return ApiService.patch(true, `Quotes/${id}/updateDraft`, data);
}

export function substituteLineItem(id, lineItemId, newLineItem, isCustom) {
    return QuoteService.substituteLineItem(
        id,
        lineItemId,
        newLineItem,
        isCustom
    );
}

/**
 * determines if line item action substitute is available
 * @param {String} quoteStatusId
 * @param {String} lineItemStatusId
 * @param {Object} lineItem
 * @returns {Boolean}
 */
export function shouldDisplayLineItemActionSubstitute(
    quoteStatusId,
    lineItemStatusId,
    lineItem
) {
    if (!quoteStatusId || !lineItemStatusId || !lineItem) {
        return false;
    }

    const acceptableQuoteStatuses = [
        'DRAFTED',
        'CREATED',
        'SUBMITTED',
        'REVIEWED',
        'REVISING',
    ];
    const acceptableProductCategoryIds = ['EQUIPMENT', 'PART', 'LABOR'];
    const acceptableProductSubCategoryIds = ['EQUIPMENT', 'PART'];
    const acceptableLineItemStatuses = ['CREATED', 'APPROVED'];
    if (
        !isMotiliAccount() ||
        !acceptableQuoteStatuses.includes(quoteStatusId) ||
        !acceptableLineItemStatuses.includes(lineItemStatusId) ||
        !lineItem.bundleJobLineItemId
    ) {
        return false;
    }
    if (
        lineItem.productCategoryId === 'CUSTOM' &&
        acceptableProductSubCategoryIds.includes(lineItem.productSubCategoryId)
    ) {
        return true;
    }
    return acceptableProductCategoryIds.includes(lineItem.productCategoryId);
}

export function getLineItemSummary(quoteId) {
    return ApiService.get(true, `Quotes/${quoteId}/lineItemSummary`);
}

export function shouldDisplayActionManageContractor(quote) {
    const { statusId } = quote;
    const acceptableStatuses = ['CREATED', 'REVIEWED', 'REVISING', 'SUBMITTED'];
    return (
        statusId && acceptableStatuses.includes(statusId) && isMotiliAccount()
    );
}

export function getUnassignReasonTypes() {
    return QuoteService.getUnassignReasonTypes();
}

export function calculateTax(quoteId) {
    return ApiService.post(true, `Quotes/${quoteId}/calculateTax`);
}
